import { Modal, Row, Col, Button } from "react-bootstrap";
import { Formik, Form, Field } from "formik";
import PlaceholderImage from "../../../assets/Images/placeholder-image.png";

const styles = {
  headerContent: {
    paddingRight: "22px",
    width: "100%",
  },
  titleWrap: {
    alignItems: "center",
    display: "flex",
    gap: "12px",
  },
  titleIcon: {
    alignItems: "center",
    border: "2px solid #ff4f12",
    borderRadius: "8px",
    color: "#ff4f12",
    display: "inline-flex",
    fontSize: "18px",
    height: "36px",
    justifyContent: "center",
    width: "36px",
  },
  subtitle: {
    color: "#586270",
    fontSize: "13px",
    fontWeight: 600,
    margin: "2px 0 0",
  },
  badgeStrip: {
    display: "grid",
    gap: "10px",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    marginTop: "14px",
  },
  metaBadge: {
    alignItems: "center",
    backgroundColor: "#fff",
    border: "1px solid #edf0f3",
    borderRadius: "6px",
    boxShadow: "0 8px 20px rgba(18, 38, 63, 0.04)",
    display: "flex",
    gap: "8px",
    minHeight: "56px",
    maxWidth: "100%",
    overflow: "hidden",
    padding: "9px 10px",
  },
  metaBadgeIcon: {
    color: "#ff4f12",
    flex: "0 0 auto",
    fontSize: "19px",
    lineHeight: 1,
  },
  metaBadgeLabel: {
    color: "#5c6670",
    display: "block",
    fontSize: "10px",
    fontWeight: 700,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  metaBadgeValue: {
    color: "#263238",
    display: "block",
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: 1.25,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  statusBadgeValue: {
    backgroundColor: "#dff2da",
    borderRadius: "12px",
    color: "#216b2a",
    display: "inline-block",
    fontSize: "11px",
    marginTop: "3px",
    padding: "2px 8px",
  },
  financerBadgeValue: {
    fontSize: "10px",
    fontWeight: 500,
    whiteSpace: "normal",
    wordBreak: "break-all",
  },
  modalBody: {
    backgroundColor: "#fbfcfe",
    padding: "18px",
  },
  mainGrid: {
    alignItems: "stretch",
    display: "grid",
    gap: "18px",
    gridTemplateColumns: "330px minmax(0, 1fr)",
  },
  complianceColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    minHeight: "100%",
  },
  detailsColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  section: {
    backgroundColor: "#fff",
    border: "1px solid #e7e9ee",
    borderRadius: "6px",
    padding: "14px",
    height: "100%",
  },
  businessComplianceSection: {
    flex: "1 1 auto",
  },
  sectionHeader: {
    alignItems: "center",
    borderBottom: "1px solid #edf0f3",
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "14px",
    paddingBottom: "9px",
  },
  sectionTitle: {
    color: "#263238",
    fontSize: "15px",
    fontWeight: 700,
    margin: 0,
  },
  sectionIcon: {
    color: "#ff4f12",
    fontSize: "17px",
    marginRight: "10px",
  },
  complianceCard: {
    backgroundColor: "#fbfcfd",
    border: "1px solid #e5e8ec",
    borderRadius: "6px",
    marginBottom: "10px",
    padding: "10px",
  },
  requirementTitle: {
    color: "#3a4651",
    display: "block",
    fontSize: "13px",
    fontWeight: 700,
    marginBottom: "8px",
  },
  imageTile: {
    display: "inline-flex",
    flexDirection: "column",
    margin: "0 8px 8px 0",
    width: "136px",
  },
  imageLink: {
    backgroundColor: "#fff",
    border: "1px solid #d8dde4",
    borderRadius: "6px",
    display: "block",
    overflow: "hidden",
  },
  image: {
    display: "block",
    height: "86px",
    objectFit: "cover",
    width: "134px",
  },
  fileThumbnail: {
    alignItems: "center",
    backgroundColor: "#f7f9fc",
    color: "#47525d",
    display: "flex",
    flexDirection: "column",
    height: "86px",
    justifyContent: "center",
    padding: "8px",
    textAlign: "center",
    width: "134px",
  },
  fileThumbnailIcon: {
    color: "#ff4f12",
    fontSize: "26px",
    lineHeight: 1,
    marginBottom: "6px",
  },
  fileThumbnailName: {
    display: "block",
    fontSize: "11px",
    fontWeight: 600,
    lineHeight: 1.2,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  textValue: {
    fontSize: "13px",
    minHeight: "36px",
  },
};

const getFirstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "") ?? "-";

const getDigits = (value) => String(value || "").replace(/\D/g, "");

const formatCnic = (value) => {
  const digits = getDigits(value).slice(0, 13);
  if (digits.length <= 5) return digits;
  if (digits.length <= 12) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`;
};

const isRejectedStatus = (value) => String(value || "").toLowerCase() === "rejected";

const FieldControl = ({ label, name, type = "text" }) => (
  <div className="form-group mb-3">
    <label htmlFor={name} className="mb-1 small font-weight-bold text-muted">{label}</label>
    <Field id={name} name={name} type={type} className="form-control form-control-sm" disabled />
  </div>
);

const ContactNumberField = ({ label, name }) => (
  <div className="form-group mb-3">
    <label htmlFor={name} className="mb-1 small font-weight-bold text-muted">{label}</label>
    <Field name={name}>
      {({ field }) => (
        <input
          {...field}
          id={name}
          type="text"
          inputMode="numeric"
          maxLength={11}
          className="form-control form-control-sm"
          disabled
          value={getDigits(field.value).slice(0, 11)}
          onChange={() => {}}
        />
      )}
    </Field>
  </div>
);

const CnicField = ({ label, name }) => (
  <div className="form-group mb-3">
    <label htmlFor={name} className="mb-1 small font-weight-bold text-muted">{label}</label>
    <Field name={name}>
      {({ field }) => (
        <input
          {...field}
          id={name}
          type="text"
          inputMode="numeric"
          maxLength={15}
          className="form-control form-control-sm"
          disabled
          value={formatCnic(field.value)}
          onChange={() => {}}
        />
      )}
    </Field>
  </div>
);

const getSectionIcon = (title) => {
  if (/personal information/i.test(title)) return "fas fa-user-friends";
  if (/personal compliance/i.test(title)) return "far fa-user";
  if (/business information/i.test(title)) return "fas fa-briefcase";
  if (/business compliance/i.test(title)) return "fas fa-briefcase";
  if (/address/i.test(title)) return "fas fa-map-marker-alt";
  return "fas fa-shield-alt";
};

const SectionHeader = ({ title }) => (
  <div style={styles.sectionHeader}>
    <h5 style={styles.sectionTitle}>
      <i className={getSectionIcon(title)} style={styles.sectionIcon}></i>
      {title}
    </h5>
  </div>
);

const getBadgeIcon = (label) => {
  if (/viz/i.test(label)) return "far fa-address-card";
  if (/financer/i.test(label)) return "fas fa-university";
  if (/kyc/i.test(label)) return "fas fa-shield-alt";
  if (/agreement/i.test(label)) return "far fa-file-alt";
  if (/assign/i.test(label)) return "fas fa-user-friends";
  return "fas fa-info-circle";
};

const HeaderBadge = ({ label, value, isStatus, isWide }) => (
  <span style={styles.metaBadge} title={`${label}: ${value}`}>
    <i className={getBadgeIcon(label)} style={styles.metaBadgeIcon}></i>
    <span style={{ minWidth: 0 }}>
      <span style={styles.metaBadgeLabel}>{label}</span>
      <span
        style={{
          ...styles.metaBadgeValue,
          ...(isStatus ? styles.statusBadgeValue : {}),
          ...(isWide ? styles.financerBadgeValue : {}),
        }}
      >
        {value}
      </span>
    </span>
  </span>
);

const ModalHeaderBadges = ({ values }) => {
  const kycStatus = getFirstValue(values?.kycStatus);
  const badges = [
    {
      label: "VIZ ID",
      value: getFirstValue(values?.vizId),
    },
    {
      label: "Financer ID",
      value: getFirstValue(values?.financerId),
      isWide: true,
    },
    {
      label: "KYC Status",
      value: kycStatus,
      isStatus: true,
    },
    {
      label: "Agreement Status",
      value: getFirstValue(values?.contractStatus),
      isStatus: true,
    },
    {
      label: "Assign Limit",
      value: getFirstValue(values?.assignLimit),
    },
    {
      label: "Reason of Rejection",
      value: getFirstValue(values?.reason),
      show: isRejectedStatus(kycStatus),
      isWide: true,
    },
  ].filter((badge) => badge.show !== false);

  return (
    <div style={styles.badgeStrip}>
      {badges.map((badge) => (
        <HeaderBadge
          key={badge.label}
          label={badge.label}
          value={badge.value}
          isStatus={badge.isStatus}
          isWide={badge.isWide}
        />
      ))}
    </div>
  );
};

const formatRequirementName = (name) =>
  (name || "Compliance requirement")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const isUrl = (value) => /^https?:\/\//i.test(value || "");

const isImageRequirement = (detail, value) =>
  isUrl(value) ||
  detail?.inputType === "document" ||
  /image|picture|photo|cnic|front|back|document|attachment/i.test(
    detail?.requirementName || ""
  );

const isImageFile = (url = "") => {
  const extension = String(url).split(".").pop().split("?")[0].toLowerCase();

  return [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "bmp",
    "webp",
    "svg",
    "jfif",
    "avif",
  ].includes(extension);
};

const getFileName = (url) => {
  if (!url) return "attachment";

  try {
    const pathname = new URL(url, window.location.origin).pathname;
    const name = decodeURIComponent(pathname.split("/").pop() || "");
    return name || "attachment";
  } catch {
    const parts = String(url).split("/");
    return decodeURIComponent(parts[parts.length - 1] || "attachment");
  }
};

const getFileIcon = (url = "") => {
  const extension = String(url).split(".").pop().split("?")[0].toLowerCase();

  if (extension === "pdf") return "fas fa-file-pdf";
  if (["xls", "xlsx", "csv"].includes(extension)) return "fas fa-file-excel";
  if (["doc", "docx"].includes(extension)) return "fas fa-file-word";
  return "fas fa-file-alt";
};

const AttachmentThumbnail = ({ value, label }) => {
  if (!value || isImageFile(value)) {
    return (
      <img
        className="kyc-form-img"
        style={styles.image}
        alt={label}
        src={value || PlaceholderImage}
        onError={(event) => {
          event.currentTarget.onerror = null;
          event.currentTarget.src = PlaceholderImage;
        }}
      />
    );
  }

  const fileName = getFileName(value);

  return (
    <span style={styles.fileThumbnail}>
      <i className={getFileIcon(value)} style={styles.fileThumbnailIcon}></i>
      <span style={styles.fileThumbnailName} title={fileName}>
        {fileName}
      </span>
    </span>
  );
};

const ComplianceImageControl = ({ value, label, values = [] }) => {
  if (values.length > 0) {
    return (
      <div style={{ ...styles.imageTile, width: "100%" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            alignItems: "flex-start",
          }}
        >
          {values.map((item, index) => (
            <div key={`${item}.${index}`} style={{ margin: "0 8px 8px 0", width: "136px" }}>
              <a
                href={item || PlaceholderImage}
                target="_blank"
                rel="noopener noreferrer"
                title="Open attachment"
                style={styles.imageLink}
              >
                <AttachmentThumbnail value={item} label={label} />
              </a>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.imageTile}>
      <div>
        <a
          href={value || PlaceholderImage}
          target="_blank"
          rel="noopener noreferrer"
          title="Open attachment"
          style={styles.imageLink}
        >
          <AttachmentThumbnail value={value} label={label} />
        </a>
      </div>
    </div>
  );
};

const ComplianceSection = ({ name, details, groupImageValues = false }) => {
  if (!Array.isArray(details) || details.length === 0) return null;

  return (
    <Row>
      {details.map((detail, detailIndex) => {
        const values = detail.values || [];
        const requirementLabel =
          detail.displayLabel || formatRequirementName(detail.requirementName);
        const isImageOnlyRequirement =
          values.length > 0 && values.every((value) => isImageRequirement(detail, value));
        const valuesFieldName = `${name}.${detailIndex}.values`;

        if (groupImageValues && isImageOnlyRequirement && values.length > 1) {
          return (
            <Col xs={12} key={valuesFieldName} className="mb-2">
              <div style={{ ...styles.complianceCard, height: "100%" }}>
                <strong style={styles.requirementTitle}>{requirementLabel}</strong>
                <ComplianceImageControl values={values} label={requirementLabel} />
              </div>
            </Col>
          );
        }

        return values.map((value, valueIndex) => {
          const fieldName = `${name}.${detailIndex}.values.${valueIndex}`;
          const isImage = isImageRequirement(detail, value);

          return (
            <Col
              xs={12}
              md={isImageOnlyRequirement ? 6 : 12}
              key={fieldName}
              className="mb-2"
            >
              <div style={{ ...styles.complianceCard, height: "100%" }}>
                <strong style={styles.requirementTitle}>{requirementLabel}</strong>
                {isImage ? (
                  <div className="d-flex justify-content-center">
                    <ComplianceImageControl value={value} label={requirementLabel} />
                  </div>
                ) : detail.dataType === "enum" && Array.isArray(detail.enumValues) ? (
                  <Field
                    as="select"
                    name={fieldName}
                    className="form-control form-control-sm"
                    disabled
                    style={styles.textValue}
                  >
                    <option value="">Select {requirementLabel}</option>
                    {detail.enumValues.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Field>
                ) : (
                  <Field
                    name={fieldName}
                    className="form-control form-control-sm"
                    disabled
                    style={styles.textValue}
                  />
                )}
              </div>
            </Col>
          );
        });
      })}
    </Row>
  );
};

const KYCListingFormComplianceViewModal = ({ show, onHide, modalValues }) => {
  return (
    <Modal show={show} onHide={onHide} backdrop="static" keyboard={false} size="xl">
      <Formik enableReinitialize initialValues={modalValues} onSubmit={() => {}}>
        {(formik) => {
          return (
            <Form autoComplete="off">
              <Modal.Header closeButton>
                <div style={styles.headerContent}>
                  <div style={styles.titleWrap}>
                    <span style={styles.titleIcon}>
                      <i className="fas fa-shield-alt"></i>
                    </span>
                    <div>
                      <Modal.Title>KYC Form</Modal.Title>
                      <p style={styles.subtitle}>Customer verification details</p>
                    </div>
                  </div>
                  <ModalHeaderBadges values={formik.values} />
                </div>
              </Modal.Header>
              <Modal.Body style={styles.modalBody}>
                <div style={styles.mainGrid}>
                  <div style={styles.complianceColumn}>
                    <div style={styles.section}>
                      <SectionHeader title="Personal Compliance" />
                      <ComplianceSection
                        name="personalDetails.complianceDetails"
                        details={formik.values.personalDetails?.complianceDetails}
                      />
                    </div>
                    <div style={{ ...styles.section, ...styles.businessComplianceSection }}>
                      <SectionHeader title="Business Compliance" />
                      <ComplianceSection
                        name="businessDetails.complianceDetails"
                        details={formik.values.businessDetails?.complianceDetails}
                        groupImageValues
                      />
                    </div>
                  </div>

                  <div style={styles.detailsColumn}>
                    <div style={styles.section}>
                      <SectionHeader title="Personal Information" />
                      <Row>
                        <Col xs={12} md={6}><FieldControl label="Name" name="personalDetails.name" /></Col>
                        <Col xs={12} md={6}><ContactNumberField label="Contact Number" name="personalDetails.contactNumber" /></Col>
                      </Row>
                      <Row>
                        <Col xs={12} md={6}><FieldControl label="Email" name="personalDetails.email" type="email" /></Col>
                        <Col xs={12} md={6}><FieldControl label="Date of Birth" name="personalDetails.dob" type="date" /></Col>
                      </Row>
                      <Row>
                        <Col xs={12} md={6}><CnicField label="CNIC" name="personalDetails.cnic" /></Col>
                      </Row>
                    </div>
                    <div style={styles.section}>
                      <SectionHeader title="Business Information" />
                      <Row>
                        <Col xs={12} md={6}><FieldControl label="Business Name" name="businessDetails.businessName" /></Col>
                        <Col xs={12} md={6}>
                          <div className="form-group mb-3">
                            <label className="mb-1 small font-weight-bold text-muted">
                              Business Type
                            </label>

                            <Field
                              as="select"
                              name="businessDetails.businessType"
                              className="form-control form-control-sm"
                              disabled
                            >
                              <option value="individual">Individual</option>
                            </Field>
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12} md={6}><CnicField label="CNIC" name="businessDetails.cnic" /></Col>
                        <Col xs={12} md={6}><FieldControl label="Credit Limit Required" name="creditLimitRequired" type="number" /></Col>
                      </Row>
                    </div>
                    <div style={styles.section}>
                      <SectionHeader title="Address & Coordinates" />
                      <Row>
                        <Col xs={12}><FieldControl label="Street Address" name="businessDetails.businessAddress.streetAddress" /></Col>
                      </Row>
                      <Row>
                        <Col xs={12} md={6}><FieldControl label="City" name="businessDetails.businessAddress.city" /></Col>
                        <Col xs={12} md={6}><FieldControl label="State" name="businessDetails.businessAddress.state" /></Col>
                      </Row>
                      <Row>
                        <Col xs={12} md={6}><FieldControl label="Country" name="businessDetails.businessAddress.country" /></Col>
                        <Col xs={12} md={3}><FieldControl label="Latitude" name="businessDetails.businessAddress.coordinates.lat" type="number" /></Col>
                        <Col xs={12} md={3}><FieldControl label="Longitude" name="businessDetails.businessAddress.coordinates.long" type="number" /></Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Close</Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default KYCListingFormComplianceViewModal;
